import Head from 'next/head'
import Link from 'next/link'
import React, { useMemo, useState } from 'react'
import { wasteGlossaryTerms } from '@src/constants/wasteGlossary'
import classes from '@src/styles/glossary.module.css'

const sortedTerms = [...wasteGlossaryTerms].sort((a, b) => a.term.localeCompare(b.term))
const letters = ['#', ...Array.from({ length: 26 }, (_, index) => String.fromCharCode(65 + index))]

const glossarySchema = {
  '@context': 'https://schema.org',
  '@type': 'DefinedTermSet',
  name: 'Dimension Waste Management Glossary',
  description: 'Plain-English definitions for junk removal, dumpster rental, and waste management terms.',
  url: 'https://getdimension.com/glossary',
  hasDefinedTerm: wasteGlossaryTerms.map((term) => ({
    '@type': 'DefinedTerm',
    name: term.term,
    termCode: term.slug,
    description: term.definition,
    url: `https://getdimension.com/glossary/${term.slug}`,
  })),
}

function GlossaryIndex() {
  return (
    <>
      <Head>
        <title>Waste Management Glossary | Dimension</title>
        <meta
          name='description'
          content='Clear definitions for junk removal, dumpster rental, waste removal, recycling, and waste management terms from Dimension.'
        />
        <link rel='canonical' href='https://getdimension.com/glossary' />
        <meta property='og:title' content='Waste Management Glossary | Dimension' />
        <meta
          property='og:description'
          content='Clear definitions for junk removal, dumpster rental, waste removal, recycling, and waste management terms from Dimension.'
        />
        <meta property='og:url' content='https://getdimension.com/glossary' />
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(glossarySchema) }} />
      </Head>
      <main className={classes.page}>
        <div className={classes.inner}>
          <nav className={classes.breadcrumb} aria-label='Breadcrumb'>
            <Link href='/'>Dimension</Link>
            <span aria-hidden='true'>/</span>
            <span>Glossary</span>
          </nav>

          <GlossaryDirectory />
        </div>
      </main>
    </>
  )
}

function GlossaryDirectory() {
  const [query, setQuery] = useState('')
  const [activeLetter, setActiveLetter] = useState('All')
  const normalizedQuery = query.trim().toLowerCase()
  const matchingTerms = useMemo(
    () =>
      sortedTerms.filter((term) => {
        const relatedNames = term.related.join(' ')
        const matchesSearch = !normalizedQuery || `${term.term} ${term.definition} ${relatedNames}`.toLowerCase().includes(normalizedQuery)
        return matchesSearch
      }),
    [normalizedQuery]
  )
  const availableLetters = useMemo(
    () => new Set(matchingTerms.map((term) => (/^[A-Z]/i.test(term.term[0]) ? term.term[0].toUpperCase() : '#'))),
    [matchingTerms]
  )
  const selectedLetter = activeLetter !== 'All' && !availableLetters.has(activeLetter) ? 'All' : activeLetter
  const filteredTerms = matchingTerms.filter(
    (term) => selectedLetter === 'All' || term.term[0].toUpperCase() === selectedLetter || (selectedLetter === '#' && !/[A-Z]/i.test(term.term[0]))
  )
  const groupedTerms = filteredTerms.reduce<Record<string, typeof sortedTerms>>((groups, term) => {
    const firstLetter = /^[A-Z]/i.test(term.term[0]) ? term.term[0].toUpperCase() : '#'
    return { ...groups, [firstLetter]: [...(groups[firstLetter] || []), term] }
  }, {})

  return (
    <>
      <header className={classes.directoryHeader}>
        <p className={classes.eyebrow}>Dimension glossary</p>
        <h1 className={classes.title}>Waste management glossary</h1>
        <p className={classes.intro}>
          Clear definitions for junk removal, dumpster rental, waste removal, recycling, and the language behind better waste decisions.
        </p>
        <div className={classes.searchRow}>
          <label className={classes.searchLabel} htmlFor='glossary-search'>
            <span className={classes.srOnly}>Search the glossary</span>
            <input
              className={classes.searchInput}
              id='glossary-search'
              type='search'
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setActiveLetter('All')
              }}
              placeholder='Search the glossary...'
            />
          </label>
          <span className={classes.resultCount} aria-live='polite'>
            {filteredTerms.length ? `1-${filteredTerms.length}` : '0'} of {filteredTerms.length}
          </span>
        </div>
        <nav className={classes.alphabetBar} aria-label='Filter glossary by first letter'>
          <button
            className={`${classes.letterButton} ${selectedLetter === 'All' ? classes.activeLetter : ''}`}
            type='button'
            aria-pressed={selectedLetter === 'All'}
            onClick={() => setActiveLetter('All')}
          >
            All
          </button>
          {letters.map((letter) => (
            <button
              className={`${classes.letterButton} ${selectedLetter === letter ? classes.activeLetter : ''}`}
              type='button'
              aria-pressed={selectedLetter === letter}
              disabled={!availableLetters.has(letter)}
              key={letter}
              onClick={() => setActiveLetter(letter)}
            >
              {letter}
            </button>
          ))}
        </nav>
      </header>

      <section className={classes.directoryResults} aria-live='polite'>
        {Object.entries(groupedTerms).map(([letter, terms]) => (
          <section className={classes.letterGroup} key={letter}>
            <h2 className={classes.letterHeading}>{letter}</h2>
            <div>
              {terms.map((term) => (
                <article className={classes.termRow} key={term.slug}>
                  <div className={classes.termInfo}>
                    <Link className={classes.termLink} href={`/glossary/${term.slug}`}>
                      {term.term}
                    </Link>
                    <p className={classes.also}>
                      Also: {term.related.map((slug) => sortedTerms.find((related) => related.slug === slug)?.term).filter(Boolean).join(', ') || 'none'}
                    </p>
                    <span className={classes.termCategory}>{term.category}</span>
                  </div>
                  <p className={classes.termDefinition}>{term.definition}</p>
                </article>
              ))}
            </div>
          </section>
        ))}
        {!filteredTerms.length && <p className={classes.emptyState}>No glossary terms match that search.</p>}
      </section>
    </>
  )
}

export default GlossaryIndex
